import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Utensils, ShoppingCart, Globe, History, CheckCircle, X,
  Search, Trash2, Settings, Menu, Home, Plus, Flame, Clock,
  ChevronDown, Package, CreditCard, Banknote, ImagePlus
} from 'lucide-react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getFirestore, collection, addDoc, serverTimestamp,
  query, where, orderBy, onSnapshot
} from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';

/* ─────────────────────────────────────
   安全加密 & 工具
───────────────────────────────────── */
const SECRET_SALT = "lico-safe-pass-";
const decodeTable = (encoded: string) => {
  try {
    const decoded = atob(encoded);
    return decoded.startsWith(SECRET_SALT) ? decoded.replace(SECRET_SALT, "") : "INVALID";
  } catch { return "INVALID"; }
};
const generateSessionCode = () => Math.floor(1000 + Math.random() * 9000).toString();

/* ─────────────────────────────────────
   Firebase
───────────────────────────────────── */
const firebaseConfig = {
  apiKey: "AIzaSyAae8vxMBB9HYiY8rs0LbmuTLFNa0ZRSEM",
  authDomain: "fastcut-d74af.firebaseapp.com",
  projectId: "fastcut-d74af",
  storageBucket: "fastcut-d74af.firebasestorage.app",
  messagingSenderId: "730435244688",
  appId: "1:730435244688:web:59311883adc59f2b3efe15"
};
const firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);
const APP_ID = "restaurant-pos-v1";
const CART_KEY = 'lico_cart_v2';
const SESSION_KEY = 'lico_session_v1';

/* ─────────────────────────────────────
   i18n — 9 語系
───────────────────────────────────── */
const i18n = {
  zh: {
    home:"主頁面", table:"桌號", history:"訂單歷史", settings:"設定",
    cart:"購物車", total:"總計", search:"搜尋菜單", all:"全部",
    hot_title:"熱門 TOP 10", clear:"清空", empty:"購物車是空的",
    invalid:"無效桌號，請重新掃描 QR Code",
    lang:"語言", fontSize:"文字大小", addMore:"我要加點", checkout:"前往結帳",
    cashPay:"現場現金付款", orderNum:"訂單號碼", orderDetail:"訂單明細",
    sessionCode:"入座序號", sessionDesc:"和朋友分享此序號一起點餐",
    sessionNext:"開始點餐", sessionPaid:"結帳後序號將自動消除",
    payMethod:"付款方式", cash:"現金付款", confirmOrder:"確認送出",
    orderSent:"餐廳已接受您的訂單", preparingMsg:"付款後開始備餐",
    tableLabel:"內用桌號", orderNoLabel:"訂單號碼", viewDetail:"餐點內容",
    backMenu:"返回選單", status_new:"已送出", status_preparing:"備餐中",
    status_done:"完成", noOrders:"尚無訂單紀錄", uploadBanner:"上傳橫幅圖片",
  },
  en: {
    home:"Home", table:"Table", history:"History", settings:"Settings",
    cart:"Cart", total:"Total", search:"Search menu", all:"All",
    hot_title:"Hot TOP 10", clear:"Clear", empty:"Cart is empty",
    invalid:"Invalid table. Please scan QR again.",
    lang:"Language", fontSize:"Font Size", addMore:"Add More", checkout:"Checkout",
    cashPay:"Pay at Counter", orderNum:"Order No.", orderDetail:"Order Detail",
    sessionCode:"Session Code", sessionDesc:"Share this code with friends to order together",
    sessionNext:"Start Ordering", sessionPaid:"Code clears after payment",
    payMethod:"Payment Method", cash:"Cash Payment", confirmOrder:"Confirm Order",
    orderSent:"Restaurant received your order", preparingMsg:"Preparing after payment",
    tableLabel:"Table", orderNoLabel:"Order No.", viewDetail:"Items",
    backMenu:"Back to Menu", status_new:"Sent", status_preparing:"Preparing",
    status_done:"Done", noOrders:"No orders yet", uploadBanner:"Upload Banner",
  },
  ja: {
    home:"ホーム", table:"テーブル", history:"履歴", settings:"設定",
    cart:"カート", total:"合計", search:"検索", all:"すべて",
    hot_title:"人気 TOP 10", clear:"クリア", empty:"カートは空です",
    invalid:"無効なテーブルコード",
    lang:"言語", fontSize:"文字サイズ", addMore:"追加注文", checkout:"会計へ",
    cashPay:"現金払い", orderNum:"注文番号", orderDetail:"注文明細",
    sessionCode:"セッションコード", sessionDesc:"友達とこのコードを共有",
    sessionNext:"注文開始", sessionPaid:"支払後にコードが消えます",
    payMethod:"支払方法", cash:"現金払い", confirmOrder:"注文確定",
    orderSent:"ご注文を受け付けました", preparingMsg:"お支払後に調理開始",
    tableLabel:"テーブル", orderNoLabel:"注文番号", viewDetail:"注文内容",
    backMenu:"メニューへ戻る", status_new:"送信済み", status_preparing:"調理中",
    status_done:"完了", noOrders:"注文履歴なし", uploadBanner:"バナー画像",
  },
  ko: {
    home:"홈", table:"테이블", history:"주문내역", settings:"설정",
    cart:"장바구니", total:"합계", search:"검색", all:"전체",
    hot_title:"인기 TOP 10", clear:"초기화", empty:"장바구니가 비었습니다",
    invalid:"유효하지 않은 테이블",
    lang:"언어", fontSize:"글자 크기", addMore:"추가 주문", checkout:"결제하기",
    cashPay:"현장 결제", orderNum:"주문 번호", orderDetail:"주문 내역",
    sessionCode:"세션 코드", sessionDesc:"친구와 코드를 공유하세요",
    sessionNext:"주문 시작", sessionPaid:"결제 후 코드가 사라집니다",
    payMethod:"결제 수단", cash:"현금 결제", confirmOrder:"주문 확인",
    orderSent:"주문이 접수되었습니다", preparingMsg:"결제 후 조리 시작",
    tableLabel:"테이블", orderNoLabel:"주문 번호", viewDetail:"주문 내용",
    backMenu:"메뉴로 돌아가기", status_new:"전송됨", status_preparing:"준비중",
    status_done:"완료", noOrders:"주문 내역 없음", uploadBanner:"배너 업로드",
  },
  vi: {
    home:"Trang chủ", table:"Bàn", history:"Lịch sử", settings:"Cài đặt",
    cart:"Giỏ hàng", total:"Tổng", search:"Tìm kiếm món", all:"Tất cả",
    hot_title:"Món hot TOP 10", clear:"Xóa", empty:"Giỏ hàng trống",
    invalid:"Mã bàn không hợp lệ",
    lang:"Ngôn ngữ", fontSize:"Cỡ chữ", addMore:"Thêm món", checkout:"Thanh toán",
    cashPay:"Trả tiền mặt", orderNum:"Số đơn", orderDetail:"Chi tiết đơn",
    sessionCode:"Mã phiên", sessionDesc:"Chia sẻ mã để bạn bè cùng gọi món",
    sessionNext:"Bắt đầu gọi món", sessionPaid:"Mã xóa sau khi thanh toán",
    payMethod:"Thanh toán", cash:"Tiền mặt", confirmOrder:"Xác nhận đơn",
    orderSent:"Nhà hàng đã nhận đơn", preparingMsg:"Chuẩn bị sau thanh toán",
    tableLabel:"Bàn", orderNoLabel:"Số đơn", viewDetail:"Món ăn",
    backMenu:"Quay lại menu", status_new:"Đã gửi", status_preparing:"Đang làm",
    status_done:"Xong", noOrders:"Chưa có đơn", uploadBanner:"Tải ảnh banner",
  },
  th: {
    home:"หน้าหลัก", table:"โต๊ะ", history:"ประวัติ", settings:"ตั้งค่า",
    cart:"ตะกร้า", total:"ยอดรวม", search:"ค้นหาเมนู", all:"ทั้งหมด",
    hot_title:"ยอดนิยม TOP 10", clear:"ล้าง", empty:"ตะกร้าว่างเปล่า",
    invalid:"รหัสโต๊ะไม่ถูกต้อง",
    lang:"ภาษา", fontSize:"ขนาดตัวอักษร", addMore:"สั่งเพิ่ม", checkout:"ชำระเงิน",
    cashPay:"ชำระเงินสด", orderNum:"เลขที่ออเดอร์", orderDetail:"รายละเอียดออเดอร์",
    sessionCode:"รหัสประจำที่นั่ง", sessionDesc:"แชร์รหัสนี้ให้เพื่อนสั่งร่วมกัน",
    sessionNext:"เริ่มสั่งอาหาร", sessionPaid:"รหัสจะถูกลบหลังชำระเงิน",
    payMethod:"วิธีชำระ", cash:"ชำระเงินสด", confirmOrder:"ยืนยันออเดอร์",
    orderSent:"ร้านได้รับออเดอร์แล้ว", preparingMsg:"เริ่มเตรียมหลังชำระ",
    tableLabel:"โต๊ะ", orderNoLabel:"เลขออเดอร์", viewDetail:"รายการอาหาร",
    backMenu:"กลับเมนู", status_new:"ส่งแล้ว", status_preparing:"กำลังทำ",
    status_done:"เสร็จ", noOrders:"ยังไม่มีออเดอร์", uploadBanner:"อัพโหลดแบนเนอร์",
  },
  id: {
    home:"Beranda", table:"Meja", history:"Riwayat", settings:"Pengaturan",
    cart:"Keranjang", total:"Total", search:"Cari menu", all:"Semua",
    hot_title:"Populer TOP 10", clear:"Hapus", empty:"Keranjang kosong",
    invalid:"Kode meja tidak valid",
    lang:"Bahasa", fontSize:"Ukuran Teks", addMore:"Tambah Pesanan", checkout:"Bayar",
    cashPay:"Bayar Tunai", orderNum:"No. Pesanan", orderDetail:"Detail Pesanan",
    sessionCode:"Kode Sesi", sessionDesc:"Bagikan kode ini ke teman",
    sessionNext:"Mulai Pesan", sessionPaid:"Kode hilang setelah bayar",
    payMethod:"Metode Bayar", cash:"Tunai", confirmOrder:"Konfirmasi Pesanan",
    orderSent:"Restoran menerima pesanan", preparingMsg:"Disiapkan setelah bayar",
    tableLabel:"Meja", orderNoLabel:"No. Pesanan", viewDetail:"Item Pesanan",
    backMenu:"Kembali ke Menu", status_new:"Terkirim", status_preparing:"Diproses",
    status_done:"Selesai", noOrders:"Belum ada pesanan", uploadBanner:"Upload Banner",
  },
  ms: {
    home:"Utama", table:"Meja", history:"Sejarah", settings:"Tetapan",
    cart:"Troli", total:"Jumlah", search:"Cari menu", all:"Semua",
    hot_title:"Popular TOP 10", clear:"Kosongkan", empty:"Troli kosong",
    invalid:"Kod meja tidak sah",
    lang:"Bahasa", fontSize:"Saiz Teks", addMore:"Tambah Pesanan", checkout:"Bayar",
    cashPay:"Bayar Tunai", orderNum:"No. Pesanan", orderDetail:"Butiran Pesanan",
    sessionCode:"Kod Sesi", sessionDesc:"Kongsi kod ini dengan rakan",
    sessionNext:"Mula Pesan", sessionPaid:"Kod dipadam selepas bayar",
    payMethod:"Kaedah Bayaran", cash:"Tunai", confirmOrder:"Sahkan Pesanan",
    orderSent:"Restoran terima pesanan", preparingMsg:"Disiapkan selepas bayar",
    tableLabel:"Meja", orderNoLabel:"No. Pesanan", viewDetail:"Item",
    backMenu:"Kembali ke Menu", status_new:"Dihantar", status_preparing:"Diproses",
    status_done:"Selesai", noOrders:"Tiada pesanan lagi", uploadBanner:"Muat naik Banner",
  },
  tl: {
    home:"Tahanan", table:"Mesa", history:"Kasaysayan", settings:"Mga Setting",
    cart:"Cart", total:"Kabuuan", search:"Maghanap ng pagkain", all:"Lahat",
    hot_title:"Sikat TOP 10", clear:"I-clear", empty:"Walang laman ang cart",
    invalid:"Di-wastong code ng mesa",
    lang:"Wika", fontSize:"Laki ng Teksto", addMore:"Magdagdag", checkout:"Bayad",
    cashPay:"Bayad sa Cash", orderNum:"Bilang ng Order", orderDetail:"Detalye ng Order",
    sessionCode:"Session Code", sessionDesc:"Ibahagi ang code sa mga kaibigan",
    sessionNext:"Magsimulang Umorder", sessionPaid:"Mabubura ang code pagkabayad",
    payMethod:"Paraan ng Bayad", cash:"Cash", confirmOrder:"Kumpirmahin ang Order",
    orderSent:"Tinanggap ng restaurant ang order", preparingMsg:"Ihahanda pagkabayad",
    tableLabel:"Mesa", orderNoLabel:"Bilang ng Order", viewDetail:"Mga Item",
    backMenu:"Bumalik sa Menu", status_new:"Naipadala", status_preparing:"Inihahanda",
    status_done:"Tapos na", noOrders:"Wala pang order", uploadBanner:"Mag-upload ng Banner",
  },
} as const;

type LangType = keyof typeof i18n;

const LANG_NAMES: Record<LangType, string> = {
  zh:'繁體中文', en:'English', ja:'日本語', ko:'한국어',
  vi:'Tiếng Việt', th:'ภาษาไทย', id:'Bahasa Indonesia',
  ms:'Bahasa Melayu', tl:'Filipino'
};

interface MenuItem { id:string; name:Record<LangType,string>; price:number; categoryId:string; image:string; isHot?:boolean; }
interface Category  { id:string; name:Record<LangType,string>; }
interface CartItem  { item:MenuItem; count:number; }
interface Order     { id:string; table:string; items:{name:string;price:number;count:number}[]; total:number; status:string; timestamp:any; orderNo?:number; }

const CATEGORIES: Category[] = [
  { id:'cat_main',  name:{zh:'A.主餐類',en:'A.Mains',ja:'A.メイン',ko:'A.메인',vi:'A.Chính',th:'A.หลัก',id:'A.Utama',ms:'A.Utama',tl:'A.Pangunahin'} },
  { id:'cat_fried', name:{zh:'B.酥炸類',en:'B.Fried', ja:'B.揚げ物',ko:'B.튀김',vi:'B.Chiên',th:'B.ทอด',id:'B.Goreng',ms:'B.Goreng',tl:'B.Prito'} },
  { id:'cat_drink', name:{zh:'D.飲料類',en:'D.Drinks',ja:'D.飲み物',ko:'D.음료',vi:'D.Nước',th:'D.เครื่องดื่ม',id:'D.Minuman',ms:'D.Minuman',tl:'D.Inumin'} },
  { id:'cat_hot',   name:{zh:'F.熱門品項',en:'F.Hot Items',ja:'F.人気',ko:'F.인기',vi:'F.Hot',th:'F.ยอดนิยม',id:'F.Populer',ms:'F.Popular',tl:'F.Sikat'} },
];

const MENU_DATA: MenuItem[] = [
  { id:'1', categoryId:'cat_main',  price:180, image:'🍔', isHot:true,  name:{zh:'經典牛肉堡',en:'Classic Beef Burger',ja:'牛バーガー',ko:'비프 버거',vi:'Burger Bò',th:'เบอร์เกอร์เนื้อ',id:'Burger Sapi',ms:'Burger Lembu',tl:'Beef Burger'} },
  { id:'2', categoryId:'cat_fried', price:80,  image:'🍟', isHot:true,  name:{zh:'法式炸薯條',en:'French Fries',ja:'フライドポテト',ko:'감자튀김',vi:'Khoai Tây Chiên',th:'เฟรนช์ฟรายส์',id:'Kentang Goreng',ms:'Kentang Goreng',tl:'French Fries'} },
  { id:'3', categoryId:'cat_drink', price:45,  image:'🥤', isHot:false, name:{zh:'冰檸檬紅茶',en:'Iced Lemon Tea',ja:'レモンティー',ko:'레몬 티',vi:'Trà Chanh',th:'ชาดำมะนาวเย็น',id:'Es Teh Lemon',ms:'Teh Lemon Ais',tl:'Iced Lemon Tea'} },
  { id:'4', categoryId:'cat_main',  price:220, image:'🍕', isHot:true,  name:{zh:'義式臘腸披薩',en:'Pepperoni Pizza',ja:'ペパロニピザ',ko:'페퍼로니 피자',vi:'Pizza Pepperoni',th:'พิซซ่าเปปเปอโรนี',id:'Pizza Pepperoni',ms:'Pizza Pepperoni',tl:'Pepperoni Pizza'} },
];

/* ─────────────────────────────────────
   Session Code 管理（localStorage）
───────────────────────────────────── */
const getOrCreateSession = (tableNum: string): string => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj.table === tableNum && obj.code && !obj.paid) return obj.code;
    }
  } catch {}
  const code = generateSessionCode();
  localStorage.setItem(SESSION_KEY, JSON.stringify({ table: tableNum, code, paid: false }));
  return code;
};

const markSessionPaid = (tableNum: string) => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    if (obj.table === tableNum) {
      localStorage.setItem(SESSION_KEY, JSON.stringify({ ...obj, paid: true }));
    }
  } catch {}
};

const isSessionPaid = (tableNum: string): boolean => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return true;
    const obj = JSON.parse(raw);
    return obj.table === tableNum ? !!obj.paid : true;
  } catch { return true; }
};

/* ═══════════════════════════════════════
   主元件
═══════════════════════════════════════ */
type ViewType = 'session'|'menu'|'history'|'settings'|'cart'|'checkout'|'success'|'search';

function App() {
  const [view, setView] = useState<ViewType>('session');
  const [lang, setLang] = useState<LangType>('zh');
  const [fontSize, setFontSize] = useState<'sm'|'base'|'lg'|'xl'>('base');
  const [selectedCat, setSelectedCat] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedbackItem, setFeedbackItem] = useState<string|null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState<string|null>(null);
  const [bannerImg, setBannerImg] = useState<string|null>(null);
  const [lastOrder, setLastOrder] = useState<Order|null>(null);
  const [showDetailInSuccess, setShowDetailInSuccess] = useState(false);
  const bannerRef = useRef<HTMLInputElement>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const tParam = urlParams.get('t') || '';
  const tableNum = useMemo(() => decodeTable(tParam), [tParam]);
  const sessionCode = useMemo(() => tableNum !== 'INVALID' ? getOrCreateSession(tableNum) : '', [tableNum]);
  const t = i18n[lang];

  // 購物車持久化
  const [cart, setCart] = useState<CartItem[]>(() => {
    try { const r = localStorage.getItem(CART_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
  });
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  // 橫幅圖片
  useEffect(() => {
    const saved = localStorage.getItem('lico_banner');
    if (saved) setBannerImg(saved);
    signInAnonymously(auth).catch(console.error);
  }, []);

  // 訂單歷史
  useEffect(() => {
    if (view !== 'history' || tableNum === 'INVALID') return;
    setOrdersLoading(true);
    const q = query(
      collection(db, 'artifacts', APP_ID, 'public', 'data', 'orders'),
      where('table','==',tableNum), orderBy('timestamp','desc')
    );
    const unsub = onSnapshot(q, snap => {
      setOrders(snap.docs.map(d => ({ id:d.id, ...d.data() } as Order)));
      setOrdersLoading(false);
    }, () => setOrdersLoading(false));
    return () => unsub();
  }, [view, tableNum]);

  const filteredMenu = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return MENU_DATA.filter(item => {
      if (view === 'search' && q) return item.name[lang].toLowerCase().includes(q);
      if (selectedCat === 'cat_hot') return !!item.isHot;
      return selectedCat === 'all' || item.categoryId === selectedCat;
    });
  }, [selectedCat, searchQuery, lang, view]);

  const addToCart  = (item:MenuItem) => { setCart(p => { const i=p.findIndex(c=>c.item.id===item.id); return i>=0?p.map((c,j)=>j===i?{...c,count:c.count+1}:c):[...p,{item,count:1}]; }); setFeedbackItem(item.id); setTimeout(()=>setFeedbackItem(null),800); };
  const increment  = (id:string) => setCart(p=>p.map(c=>c.item.id===id?{...c,count:c.count+1}:c));
  const decrement  = (id:string) => setCart(p=>p.flatMap(c=>c.item.id===id?(c.count>1?[{...c,count:c.count-1}]:[]):c));
  const removeItem = (id:string) => setCart(p=>p.filter(c=>c.item.id!==id));
  const clearCart  = () => setCart([]);
  const totalAmount = cart.reduce((s,c)=>s+c.item.price*c.count,0);
  const totalCount  = cart.reduce((a,b)=>a+b.count,0);

  const submitOrder = async () => {
    if (!cart.length || tableNum==='INVALID') return;
    setLoading(true);
    try {
      const orderNo = Math.floor(Math.random()*900)+100; // 100-999 三位數
      const ref = await addDoc(collection(db,'artifacts',APP_ID,'public','data','orders'), {
        table: tableNum, sessionCode,
        items: cart.map(c=>({name:c.item.name[lang],price:c.item.price,count:c.count})),
        total: totalAmount, status:'new', orderNo,
        timestamp: serverTimestamp(),
      });
      const order: Order = {
        id:ref.id, table:tableNum,
        items: cart.map(c=>({name:c.item.name[lang],price:c.item.price,count:c.count})),
        total:totalAmount, status:'new', timestamp:null, orderNo
      };
      setLastOrder(order);
      setShowDetailInSuccess(false);
      clearCart();
      setView('success');
    } catch { alert('送單失敗，請重試'); } finally { setLoading(false); }
  };

  const handleBannerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const b64 = ev.target?.result as string;
      setBannerImg(b64);
      localStorage.setItem('lico_banner', b64);
    };
    reader.readAsDataURL(file);
  };

  if (tableNum === 'INVALID') {
    return (
      <div className="h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="bg-white p-10 rounded-[40px] text-center shadow-2xl animate-in zoom-in">
          <div className="w-20 h-20 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6"><X size={40}/></div>
          <h2 className="text-2xl font-black text-slate-800 mb-2">{t.invalid}</h2>
          <p className="text-slate-400 font-bold">Please contact staff</p>
        </div>
      </div>
    );
  }

  const fontClass   = {sm:'text-sm',base:'text-base',lg:'text-lg',xl:'text-xl'}[fontSize];
  const statusColor = (s:string) => s==='done'?'bg-emerald-100 text-emerald-700':s==='preparing'?'bg-amber-100 text-amber-700':'bg-blue-100 text-blue-700';
  const statusLabel = (s:string) => s==='done'?t.status_done:s==='preparing'?t.status_preparing:t.status_new;
  const fmtTime     = (ts:any)   => ts?.toDate ? ts.toDate().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '--:--';

  return (
    <div className={`w-full h-screen bg-slate-900 flex justify-center items-center font-sans ${fontClass}`}>
      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}`}</style>

      {isSidebarOpen && <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-[60]" onClick={()=>setIsSidebarOpen(false)}/>}

      {/* ─── 側邊欄 ─── */}
      <aside className={`absolute left-0 top-0 h-full w-64 bg-white z-[70] shadow-2xl transition-transform duration-300 ${isSidebarOpen?'translate-x-0':'-translate-x-full'}`}>
        <div className="p-6 flex flex-col h-full">
          <div className="flex items-center gap-2 mb-8">
            <Utensils className="text-orange-500"/>
            <span className="font-black text-xl tracking-tighter">LICO 訂餐</span>
          </div>
          <nav className="space-y-1 flex-1">
            {([
              ['menu','home',<Home size={20}/>],
              ['history','history',<History size={20}/>],
              ['settings','settings',<Settings size={20}/>]
            ] as [string,string,React.ReactNode][]).map(([v,tk,icon])=>(
              <button key={v}
                onClick={()=>{ setView(v as ViewType); setSelectedCat('all'); setIsSidebarOpen(false); }}
                className={`w-full flex items-center gap-4 p-4 rounded-xl font-bold transition-colors ${view===v?'bg-slate-900 text-white':'text-slate-500 hover:bg-slate-50'}`}
              >{icon} {(t as any)[tk]}</button>
            ))}
          </nav>
          {/* 序號（未結帳時顯示） */}
          {!isSessionPaid(tableNum) && (
            <div className="mt-4 p-4 bg-orange-50 rounded-2xl border border-orange-100">
              <p className="text-xs font-bold text-orange-400 mb-2 uppercase tracking-widest">{t.sessionCode}</p>
              <div className="flex gap-2 justify-center">
                {sessionCode.split('').map((d,i)=>(
                  <span key={i} className="w-10 h-10 bg-white rounded-xl flex items-center justify-center font-black text-xl shadow-sm border border-orange-100">{d}</span>
                ))}
              </div>
            </div>
          )}
          <div className="p-3 bg-slate-50 rounded-2xl mt-3 text-xs font-bold flex justify-between text-slate-400">
            <span>{t.table}</span><span className="text-slate-900">{tableNum}</span>
          </div>
        </div>
      </aside>

      <div className="w-full h-full sm:max-w-[480px] bg-white shadow-2xl relative flex flex-col overflow-hidden">

        {/* ══════════════════════════════════════════
            SESSION BOTTOM SHEET（疊在菜單頁上方）
        ══════════════════════════════════════════ */}
        {view === 'session' && (
          <>
            {/* 背後模糊遮罩 */}
            <div className="absolute inset-0 z-[50] bg-black/35 backdrop-blur-[2px]" />
            {/* Bottom Sheet */}
            <div className="absolute bottom-0 left-0 right-0 z-[55] bg-white rounded-t-[36px] shadow-2xl"
              style={{animation:'slideUp 0.35s cubic-bezier(0.32,0.72,0,1)'}}>
              <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
              {/* 拖曳把手 */}
              <div className="flex justify-center pt-4 pb-1">
                <div className="w-10 h-1.5 bg-slate-200 rounded-full"/>
              </div>
              <div className="px-6 pb-10 pt-4 flex flex-col items-center gap-5">
                {/* 標題列 */}
                <div className="w-full flex items-center justify-between">
                  <h2 className="text-xl font-black text-slate-800">歡迎用餐 👋</h2>
                  <div className="flex items-center gap-1.5 text-xs font-bold text-slate-400 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
                    ？說明
                  </div>
                </div>
                <p className="text-slate-400 font-medium text-sm w-full">{t.sessionDesc}</p>

                {/* 四碼序號 */}
                <div className="flex gap-3 w-full justify-center py-1">
                  {sessionCode.split('').map((d,i)=>(
                    <div key={i} className="w-16 h-16 bg-slate-50 border-2 border-slate-200 rounded-2xl flex items-center justify-center font-black text-3xl text-slate-800 shadow-sm select-none">
                      {d}
                    </div>
                  ))}
                </div>

                <p className="text-xs text-slate-400 font-medium text-center">{t.sessionPaid}</p>

                <button
                  onClick={()=>setView('menu')}
                  className="w-full py-5 bg-slate-900 text-white rounded-2xl font-black text-lg active:scale-95 transition-transform shadow-xl"
                >
                  {t.sessionNext}
                </button>
              </div>
            </div>
          </>
        )}

        {/* HEADER（常駐顯示；session 時被遮罩覆蓋） */}
        <header className={`h-16 bg-white border-b flex items-center justify-between px-4 z-30 flex-shrink-0 ${view==='session'?'pointer-events-none':''}`}>
          <button onClick={()=>setIsSidebarOpen(true)} className="p-2 hover:bg-slate-100 rounded-full active:scale-90 transition-transform"><Menu size={24}/></button>
          <div className="font-black tracking-tighter text-slate-800">LICO RESTAURANT</div>
          <button onClick={()=>setView('cart')} className="p-2 bg-orange-50 rounded-full relative active:scale-90 transition-all">
            <ShoppingCart className="text-orange-600" size={20}/>
            {totalCount>0&&<span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full border-2 border-white">{totalCount}</span>}
          </button>
        </header>

        {/* 分類 Tabs */}
        {(view === 'menu' || view === 'session') && (
          <div className="bg-white border-b flex items-center p-3 gap-2 overflow-hidden shadow-sm flex-shrink-0">
            <button onClick={()=>setView('search')} className="w-10 h-10 bg-orange-500 text-white rounded-xl flex items-center justify-center shadow-lg active:scale-90 flex-shrink-0"><Search size={20}/></button>
            <div className="flex-1 flex gap-2 overflow-x-auto no-scrollbar">
              <button onClick={()=>setSelectedCat('all')} className={`px-4 py-2 rounded-full text-xs font-black whitespace-nowrap transition-all ${selectedCat==='all'?'bg-slate-900 text-white shadow-md':'bg-slate-100 text-slate-400'}`}>{t.all}</button>
              {CATEGORIES.map(cat=>(
                <button key={cat.id} onClick={()=>setSelectedCat(cat.id)} className={`px-4 py-2 rounded-full text-xs font-black whitespace-nowrap transition-all flex items-center gap-1 ${selectedCat===cat.id?'bg-slate-900 text-white shadow-md':'bg-slate-100 text-slate-400'}`}>
                  {cat.id==='cat_hot'&&<Flame size={12}/>} {cat.name[lang]}
                </button>
              ))}
            </div>
          </div>
        )}

        <main className="flex-1 overflow-y-auto p-4 bg-slate-50/50 no-scrollbar">

          {/* ══════ MENU ══════ */}
          {(view === 'menu' || view === 'session') && (
            <div className="space-y-4 animate-in fade-in">
              {/* 橫幅（可點擊上傳） */}
              <div className="relative w-full h-36 rounded-3xl overflow-hidden group cursor-pointer" onClick={()=>bannerRef.current?.click()}>
                {bannerImg
                  ? <img src={bannerImg} className="w-full h-full object-cover" alt="banner"/>
                  : <div className="w-full h-full bg-gradient-to-br from-orange-400 to-amber-500 flex flex-col items-center justify-center gap-2"><Utensils className="text-white/60" size={36}/><span className="text-white/80 text-xs font-bold">{t.uploadBanner}</span></div>
                }
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="bg-white/90 text-slate-800 px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2"><ImagePlus size={16}/>{t.uploadBanner}</div>
                </div>
                <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={handleBannerUpload}/>
              </div>

              <h2 className="font-black text-xl text-slate-800 flex items-center gap-2">
                {selectedCat==='cat_hot'?<><Flame className="text-orange-500"/>{t.hot_title}</>:selectedCat==='all'?'Menu':CATEGORIES.find(c=>c.id===selectedCat)?.name[lang]}
              </h2>
              <div className="grid grid-cols-1 gap-4 pb-24">
                {filteredMenu.map(item=>{
                  const entry = cart.find(c=>c.item.id===item.id);
                  return (
                    <div key={item.id} className="bg-white p-4 rounded-[28px] shadow-sm flex items-center gap-4 border border-slate-100 group">
                      <div className="text-4xl w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center group-hover:rotate-6 transition-transform flex-shrink-0">{item.image}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-800 truncate">{item.name[lang]}</h3>
                        <p className="text-orange-500 font-black text-lg">${item.price}</p>
                      </div>
                      {entry ? (
                        <div className="flex items-center gap-2 bg-slate-900 rounded-2xl px-2 py-1.5 flex-shrink-0">
                          <button onClick={()=>decrement(item.id)} className="w-7 h-7 rounded-xl bg-white/10 text-white flex items-center justify-center active:scale-90 text-lg font-bold leading-none">−</button>
                          <span className="text-white font-black text-sm w-5 text-center">{entry.count}</span>
                          <button onClick={()=>increment(item.id)} className="w-7 h-7 rounded-xl bg-orange-500 text-white flex items-center justify-center active:scale-90"><Plus size={14}/></button>
                        </div>
                      ) : (
                        <button onClick={()=>addToCart(item)} className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg transition-all active:scale-90 flex-shrink-0 ${feedbackItem===item.id?'bg-emerald-500 text-white':'bg-orange-500 text-white shadow-orange-100'}`}>
                          {feedbackItem===item.id?<CheckCircle size={20}/>:<Plus size={20}/>}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══════ SEARCH ══════ */}
          {view === 'search' && (
            <div className="space-y-4 animate-in fade-in">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20}/>
                <input autoFocus value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder={t.search} className="w-full pl-12 pr-10 py-4 bg-white border-2 border-orange-100 rounded-3xl focus:border-orange-500 outline-none font-bold"/>
                <button onClick={()=>{setView('menu');setSearchQuery('');}} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 bg-slate-100 rounded-full"><X size={16}/></button>
              </div>
              <div className="space-y-3 pb-24">
                {searchQuery && filteredMenu.map(item=>(
                  <div key={item.id} className="bg-white p-4 rounded-2xl flex items-center gap-4 border border-slate-100 shadow-sm">
                    <span className="text-3xl flex-shrink-0">{item.image}</span>
                    <div className="flex-1 font-bold truncate">{item.name[lang]}</div>
                    <div className="text-orange-500 font-black flex-shrink-0">${item.price}</div>
                    <button onClick={()=>addToCart(item)} className="w-10 h-10 bg-orange-500 text-white rounded-xl flex items-center justify-center active:scale-90 flex-shrink-0"><Plus size={18}/></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══════ CART ══════ */}
          {view === 'cart' && (
            <div className="space-y-4 animate-in slide-in-from-right pb-24">
              <div className="flex justify-between items-center">
                <h2 className="font-black text-2xl">{t.cart}</h2>
                {cart.length>0&&<button onClick={clearCart} className="text-xs font-bold text-red-400 flex items-center gap-1 bg-red-50 px-3 py-1.5 rounded-full active:scale-95"><Trash2 size={14}/>{t.clear}</button>}
              </div>
              {cart.length===0 ? (
                <div className="text-center py-24 opacity-20"><ShoppingCart size={80} className="mx-auto"/><p className="mt-4 font-black">{t.empty}</p></div>
              ) : (
                <>
                  <div className="space-y-3">
                    {cart.map(c=>(
                      <div key={c.item.id} className="bg-white p-4 rounded-[28px] shadow-sm border border-slate-50 flex items-center gap-3">
                        <div className="text-2xl w-11 h-11 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">{c.item.image}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-slate-800 truncate">{c.item.name[lang]}</div>
                          <div className="flex items-center gap-2 mt-2">
                            <button onClick={()=>decrement(c.item.id)} className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-xl active:scale-90 text-lg font-bold leading-none">−</button>
                            <span className="font-black w-5 text-center">{c.count}</span>
                            <button onClick={()=>increment(c.item.id)} className="w-8 h-8 flex items-center justify-center bg-orange-100 text-orange-600 rounded-xl active:scale-90"><Plus size={16}/></button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="font-black text-orange-600">${c.item.price*c.count}</span>
                          <button onClick={()=>removeItem(c.item.id)} className="p-1.5 text-red-300 active:scale-90"><Trash2 size={16}/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-6 bg-slate-900 rounded-[40px] text-white shadow-2xl space-y-5">
                    <div className="flex justify-between items-end">
                      <span className="opacity-50 text-xs font-bold uppercase tracking-widest">Total</span>
                      <span className="text-4xl font-black text-orange-500 tracking-tighter">${totalAmount}</span>
                    </div>
                    <button onClick={()=>setView('checkout')} className="w-full py-5 bg-orange-500 text-white rounded-2xl font-black text-lg active:scale-95 transition-all">
                      {t.checkout} →
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══════ CHECKOUT 確認訂單頁 ══════ */}
          {view === 'checkout' && (
            <div className="space-y-5 animate-in slide-in-from-right pb-24">
              <h2 className="font-black text-2xl">{t.orderDetail}</h2>

              {/* 桌號 + 訂單序號 */}
              <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100">
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  <div className="text-center pr-4">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t.tableLabel}</p>
                    <p className="text-4xl font-black text-slate-800">{tableNum}</p>
                  </div>
                  <div className="text-center pl-4">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t.sessionCode}</p>
                    <div className="flex gap-1.5 justify-center">
                      {sessionCode.split('').map((d,i)=>(
                        <span key={i} className="w-8 h-8 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-center font-black text-orange-600">{d}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* 餐點明細 */}
              <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100 space-y-3">
                <h3 className="font-black text-slate-700 flex items-center gap-2"><Package size={16} className="text-orange-500"/>{t.viewDetail}</h3>
                {cart.map((c,i)=>(
                  <div key={i} className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-lg text-[11px] font-black flex items-center justify-center">{c.count}</span>
                      <span className="font-bold text-slate-700 text-sm">{c.item.name[lang]}</span>
                    </div>
                    <span className="font-black text-slate-600 text-sm">${c.item.price*c.count}</span>
                  </div>
                ))}
                <div className="pt-3 border-t border-dashed border-slate-100 flex justify-between">
                  <span className="font-bold text-slate-400 text-xs uppercase tracking-widest self-end">{t.total}</span>
                  <span className="text-2xl font-black text-orange-500">${totalAmount}</span>
                </div>
              </div>

              {/* 付款方式 */}
              <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100">
                <h3 className="font-black text-slate-700 mb-3 flex items-center gap-2"><CreditCard size={16} className="text-orange-500"/>{t.payMethod}</h3>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl border-2 border-slate-800">
                  <Banknote size={20} className="text-slate-700"/>
                  <span className="font-black text-slate-800 flex-1">{t.cash}</span>
                  <CheckCircle size={18} className="text-emerald-500"/>
                </div>
              </div>

              {/* 操作按鈕 */}
              <div className="flex gap-3">
                <button onClick={()=>setView('cart')} className="flex-1 py-4 bg-slate-100 text-slate-700 rounded-2xl font-black active:scale-95">
                  {t.addMore}
                </button>
                <button onClick={submitOrder} disabled={loading} className="flex-1 py-4 bg-orange-500 text-white rounded-2xl font-black active:scale-95 disabled:opacity-60">
                  {loading?'...':t.confirmOrder}
                </button>
              </div>
            </div>
          )}

          {/* ══════ HISTORY ══════ */}
          {view === 'history' && (
            <div className="space-y-4 animate-in slide-in-from-right pb-24">
              <div className="flex items-center justify-between">
                <h2 className="font-black text-xl">{t.history}</h2>
                <span className="text-xs bg-slate-100 text-slate-500 font-bold px-3 py-1.5 rounded-full">{t.table} {tableNum}</span>
              </div>
              {ordersLoading ? (
                <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"/></div>
              ) : orders.length===0 ? (
                <div className="text-center py-20 opacity-20"><History size={60} className="mx-auto"/><p className="mt-4 font-black">{t.noOrders}</p></div>
              ) : (
                <>
                  <div className="bg-slate-900 text-white rounded-3xl p-5 flex justify-between">
                    <div><p className="text-xs opacity-40 uppercase tracking-widest mb-1">Total</p><p className="text-3xl font-black text-orange-400">${orders.reduce((s,o)=>s+o.total,0)}</p></div>
                    <div className="text-right"><p className="text-xs opacity-40 uppercase tracking-widest mb-1">Orders</p><p className="text-3xl font-black">{orders.length}</p></div>
                  </div>
                  {orders.map((order,idx)=>(
                    <div key={order.id} className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                      <button className="w-full p-4 flex items-center justify-between" onClick={()=>setExpandedOrder(expandedOrder===order.id?null:order.id)}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-2xl flex items-center justify-center font-black text-slate-500 text-sm">#{orders.length-idx}</div>
                          <div>
                            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${statusColor(order.status)}`}>{statusLabel(order.status)}</span>
                            <div className="flex items-center gap-1 mt-1 text-xs text-slate-400"><Clock size={10}/>{fmtTime(order.timestamp)} · {order.items?.length} 項</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-black text-orange-500">${order.total}</span>
                          <ChevronDown size={16} className={`text-slate-400 transition-transform ${expandedOrder===order.id?'rotate-180':''}`}/>
                        </div>
                      </button>
                      {expandedOrder===order.id && (
                        <div className="border-t border-slate-50 px-4 pb-4 pt-3 space-y-2">
                          {order.items?.map((oi,i)=>(
                            <div key={i} className="flex justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <span className="w-5 h-5 bg-orange-100 text-orange-600 rounded-md text-[10px] font-black flex items-center justify-center">{oi.count}</span>
                                <span className="font-medium text-slate-700">{oi.name}</span>
                              </div>
                              <span className="font-bold text-slate-500">${oi.price*oi.count}</span>
                            </div>
                          ))}
                          <div className="flex justify-between pt-2 border-t border-dashed border-slate-100">
                            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">{t.total}</span>
                            <span className="font-black text-slate-800">${order.total}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* ══════ SETTINGS ══════ */}
          {view === 'settings' && (
            <div className="space-y-6 animate-in zoom-in-95 pb-24">
              {/* 橫幅上傳 */}
              <div className="bg-white p-6 rounded-[32px] shadow-sm border border-slate-50 space-y-4">
                <h3 className="font-black flex items-center gap-2 text-slate-700"><ImagePlus size={20} className="text-orange-500"/>{t.uploadBanner}</h3>
                <div className="relative w-full h-28 rounded-2xl overflow-hidden bg-slate-50 border-2 border-dashed border-slate-200 cursor-pointer group" onClick={()=>bannerRef.current?.click()}>
                  {bannerImg
                    ? <img src={bannerImg} className="w-full h-full object-cover" alt="banner"/>
                    : <div className="w-full h-full flex flex-col items-center justify-center text-slate-300"><ImagePlus size={32}/><p className="text-xs mt-1 font-bold">點擊上傳圖片</p></div>
                  }
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                    <span className="bg-white text-slate-700 px-3 py-1 rounded-lg text-xs font-bold">更換圖片</span>
                  </div>
                </div>
                <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={handleBannerUpload}/>
              </div>

              {/* 語言 */}
              <div className="bg-white p-6 rounded-[32px] shadow-sm border border-slate-50 space-y-4">
                <h3 className="font-black flex items-center gap-2 text-slate-700"><Globe size={20} className="text-orange-500"/>{t.lang}</h3>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(i18n) as LangType[]).map(l=>(
                    <button key={l} onClick={()=>setLang(l)} className={`py-3 rounded-2xl font-bold text-sm transition-all border-2 ${lang===l?'border-orange-500 bg-orange-50 text-orange-600 shadow-md':'border-slate-50 text-slate-400'}`}>
                      {LANG_NAMES[l]}
                    </button>
                  ))}
                </div>
              </div>

              {/* 字體大小 */}
              <div className="bg-white p-6 rounded-[32px] shadow-sm border border-slate-50 space-y-4">
                <h3 className="font-black flex items-center gap-2 text-slate-700"><Settings size={20} className="text-orange-500"/>{t.fontSize}</h3>
                <div className="flex gap-2">
                  {(['sm','base','lg','xl'] as const).map(s=>(
                    <button key={s} onClick={()=>setFontSize(s)} className={`flex-1 py-4 rounded-2xl font-black text-xs transition-all border-2 ${fontSize===s?'border-orange-500 bg-orange-50 text-orange-600':'border-slate-50 text-slate-400'}`}>{s.toUpperCase()}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══════ SUCCESS 訂單明細頁 ══════ */}
          {view === 'success' && lastOrder && (
            <div className="space-y-5 animate-in zoom-in pb-24">
              {/* 成功圖示 */}
              <div className="text-center py-6">
                <div className="w-20 h-20 bg-emerald-500 rounded-[32px] flex items-center justify-center mx-auto mb-4 shadow-2xl shadow-emerald-200">
                  <CheckCircle className="text-white" size={44}/>
                </div>
                <h2 className="text-2xl font-black text-slate-800">{t.orderSent}</h2>
                <p className="text-slate-400 font-medium mt-1 text-sm">{t.preparingMsg}</p>
              </div>

              {/* 桌號 + 訂單號 */}
              <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100">
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  <div className="text-center pr-4">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t.tableLabel}</p>
                    <p className="text-4xl font-black text-slate-800">{lastOrder.table}</p>
                  </div>
                  <div className="text-center pl-4">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t.orderNoLabel}</p>
                    <p className="text-4xl font-black text-orange-500">{lastOrder.orderNo!=null?String(lastOrder.orderNo).padStart(3,'0'):'---'}</p>
                  </div>
                </div>
              </div>

              {/* 餐點內容（可展開） */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <button className="w-full p-4 flex items-center justify-between font-bold text-slate-700" onClick={()=>setShowDetailInSuccess(v=>!v)}>
                  <span className="flex items-center gap-2"><Package size={16} className="text-orange-500"/>{t.viewDetail}</span>
                  <ChevronDown size={16} className={`text-slate-400 transition-transform ${showDetailInSuccess?'rotate-180':''}`}/>
                </button>
                {showDetailInSuccess && (
                  <div className="border-t border-slate-50 px-4 pb-4 pt-3 space-y-2 animate-in slide-in-from-top-2">
                    {lastOrder.items.map((oi,i)=>(
                      <div key={i} className="flex justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 bg-orange-100 text-orange-600 rounded-md text-[10px] font-black flex items-center justify-center">{oi.count}</span>
                          <span className="font-medium text-slate-700">{oi.name}</span>
                        </div>
                        <span className="font-bold text-slate-500">${oi.price*oi.count}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t border-dashed border-slate-100">
                      <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">{t.total}</span>
                      <span className="font-black text-orange-500 text-lg">${lastOrder.total}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* 總金額 */}
              <div className="bg-slate-900 text-white p-5 rounded-3xl flex justify-between items-center">
                <span className="font-bold opacity-40 uppercase tracking-widest text-xs">{t.total}</span>
                <span className="text-3xl font-black text-orange-400">${lastOrder.total}</span>
              </div>

              {/* 操作按鈕 */}
              <div className="flex gap-3">
                <button onClick={()=>setView('menu')} className="flex-1 py-4 bg-slate-100 text-slate-700 rounded-2xl font-black active:scale-95 transition-transform">
                  {t.addMore}
                </button>
                <button onClick={()=>setView('history')} className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-black shadow-xl active:scale-95 transition-transform">
                  {t.history}
                </button>
              </div>
            </div>
          )}

        </main>

        {loading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"/>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
